import { useCallback, useEffect, useMemo, useState } from "react";
import { useBaasInstance } from "../Contexts/BaasInstanceContext";
import {
  evaluateCapability,
  isBaasFullConsoleRole,
  isBaasMasterRole,
  isReadOnlyCapabilities,
} from "../Pages/Private/BaasConsole/baasCapabilities";
import {
  applyBaasViewAsOverlay,
  readBaasViewAsPersona,
} from "../Pages/Private/BaasConsole/baasViewAs";
import {
  getBaasInstanceCapabilities,
  getBaasInstanceCapabilitiesSync,
} from "../Services/baasInstanceCapabilitiesApi";

export const useBaasCapabilities = () => {
  const { selectedInstance, selectedInstanceId, dataEpoch } = useBaasInstance();
  const [capabilities, setCapabilities] = useState(() =>
    getBaasInstanceCapabilitiesSync(selectedInstance || selectedInstanceId),
  );
  const [loading, setLoading] = useState(Boolean(selectedInstance || selectedInstanceId));
  const [viewAsTick, setViewAsTick] = useState(0);

  useEffect(() => {
    const onViewAs = () => setViewAsTick((n) => n + 1);
    window.addEventListener("baas-view-as-changed", onViewAs);
    window.addEventListener("storage", onViewAs);
    return () => {
      window.removeEventListener("baas-view-as-changed", onViewAs);
      window.removeEventListener("storage", onViewAs);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const target = selectedInstance || selectedInstanceId;

    if (!target) {
      setCapabilities(getBaasInstanceCapabilitiesSync(null));
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    (async () => {
      const next = await getBaasInstanceCapabilities(target);
      if (!cancelled) {
        setCapabilities(next);
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedInstance, selectedInstanceId, dataEpoch]);

  const effectiveCapabilities = useMemo(() => {
    const persona = readBaasViewAsPersona();
    return applyBaasViewAsOverlay(capabilities, persona);
  }, [capabilities, viewAsTick]);

  const can = useCallback(
    (capabilityId) => {
      // Product gates always run (via evaluateCapability). While loading, deny
      // so rail menus (Stables / Cartões / …) do not flash before features resolve.
      // Core panels stay usable from sync defaults (entities/api/credit on).
      if (loading && !isBaasFullConsoleRole(effectiveCapabilities)) return false;
      return evaluateCapability(effectiveCapabilities, capabilityId).allowed;
    },
    [effectiveCapabilities, loading],
  );

  const getBlockReason = useCallback(
    (capabilityId) => {
      // Avoid flashing "Sem permissão" while my-permissions is still loading.
      if (loading) return null;
      return evaluateCapability(effectiveCapabilities, capabilityId).blockReason;
    },
    [effectiveCapabilities, loading],
  );

  const isReadOnly = useMemo(
    () => isReadOnlyCapabilities(effectiveCapabilities),
    [effectiveCapabilities],
  );

  const isMaster = useMemo(
    () => isBaasMasterRole(effectiveCapabilities),
    [effectiveCapabilities],
  );

  return useMemo(
    () => ({
      capabilities: effectiveCapabilities,
      loading,
      can,
      getBlockReason,
      /** Viewer / visualizador — see everything, mutate nothing. */
      isReadOnly,
      /** Platform master (super_user) — full console. */
      isMaster,
      viewAsPersonaId: effectiveCapabilities?.viewAsPersonaId || null,
      canViewProduction: effectiveCapabilities?.canViewProduction !== false,
      canViewHomolog: effectiveCapabilities?.canViewHomolog !== false,
    }),
    [effectiveCapabilities, loading, can, getBlockReason, isReadOnly, isMaster],
  );
};
